// Back from Stripe's billing portal (D88 addendum; Console D entry screens, APS-323): the card
// the owner may have changed is checked (a card issued in Brazil on a USD subscription offers
// the switch to reais), then the plan settings open, replacing this page in the history. A
// failed check still lands there; an organization without a project goes to its first one.
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { billingCheck, billingQuery, projectsQuery } from '../api/queries'
import { EntryHead, EntryNote, EntryPage, EntryWait } from '../components/d'
import { useI18n } from '../i18n'

export function BillingReturnPage() {
  const { t } = useI18n()
  const { orgId } = useParams({ strict: false }) as { orgId: string }
  const qc = useQueryClient()
  const navigate = useNavigate()
  const started = useRef(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    void (async () => {
      const billing = await billingCheck(orgId).catch(() => undefined)
      if (billing) qc.setQueryData(billingQuery(orgId).queryKey, billing)
      const projects = await qc.ensureQueryData(projectsQuery(orgId)).catch(() => undefined)
      if (!projects) return setFailed(true)
      const first = projects[0]
      if (!first) navigate({ to: '/o/$orgId/new-project', params: { orgId }, replace: true })
      else
        navigate({
          to: '/o/$orgId/p/$projectId/settings/$section',
          params: { orgId, projectId: first.id, section: 'plan' },
          replace: true,
        })
    })()
  }, [orgId, qc, navigate])

  return (
    <EntryPage>
      <EntryHead
        kicker={t('billingReturn.kicker')}
        title={t('billingReturn.title')}
        lede={t('billingReturn.lede')}
      />
      {failed ? (
        <EntryNote tone="crash">{t('state.failed')}</EntryNote>
      ) : (
        <EntryWait>{t('billingReturn.checking')}</EntryWait>
      )}
    </EntryPage>
  )
}
